import React from 'react';
import {
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Button,
  Box,
  Stack,
  Chip,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Today as TodayIcon,
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
  ViewWeek as ViewWeekIcon,
  CalendarViewMonth as CalendarViewMonthIcon,
  CalendarViewDay as CalendarViewDayIcon,
  Lock as LockIcon,
  Info as InfoIcon 
} from '@mui/icons-material';
import { format, startOfWeek, addDays } from 'date-fns';
import { es } from 'date-fns/locale';

const CalendarControls = ({
  currentDate,
  selectedDate,
  currentView,
  especialistas,
  especialidades,
  selectedEspecialista,
  selectedEspecialidad,
  loadingHorarios,
  filtersLocked = false,
  getUserRole,
  onPrev,
  onNext,
  onToday,
  onViewChange,
  onEspecialistaChange,
  onEspecialidadChange
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const getPeriodTitle = () => {
    switch (currentView) {
      case 'month':
        return format(currentDate, 'MMMM yyyy', { locale: es });
      case 'week':
        return `Semana del ${format(startOfWeek(currentDate, { weekStartsOn: 1 }), 'd', { locale: es })} al ${format(addDays(startOfWeek(currentDate, { weekStartsOn: 1 }), 6), 'd MMMM yyyy', { locale: es })}`;
      case 'day':
        return format(selectedDate, "EEEE d 'de' MMMM 'de' yyyy", { locale: es });
      default:
        return '';
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Grid container spacing={2} alignItems="center">
          {/* Navegación anterior/siguiente */}
          <Grid item xs={12} md={3}>
            <Box display="flex">
              <Button
                variant="outlined"
                color="primary"
                startIcon={<NavigateBeforeIcon />}
                onClick={onPrev}
                sx={{ mr: 1 }}
                size={isMobile ? "small" : "medium"}
              >
                {!isMobile && "Anterior"}
              </Button>
              <Button
                variant="outlined"
                color="primary"
                endIcon={<NavigateNextIcon />}
                onClick={onNext}
                size={isMobile ? "small" : "medium"}
              >
                {!isMobile && "Siguiente"}
              </Button>
            </Box>
          </Grid>

          {/* Botón Hoy */}
          <Grid item xs={12} md={2}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<TodayIcon />}
              onClick={onToday}
              fullWidth
              size={isMobile ? "small" : "medium"}
            >
              Hoy
            </Button>
          </Grid>

          {/* Selectores de vista */}
          <Grid item xs={12} md={3}>
            <Stack direction="row" spacing={1}>
              <Button
                variant={currentView === 'month' ? 'contained' : 'outlined'}
                color="primary"
                startIcon={!isMobile && <CalendarViewMonthIcon />}
                onClick={() => onViewChange('month')}
                size={isMobile ? "small" : "medium"}
              >
                {isMobile ? "M" : "Mes"}
              </Button>
              <Button
                variant={currentView === 'week' ? 'contained' : 'outlined'}
                color="primary"
                startIcon={!isMobile && <ViewWeekIcon />}
                onClick={() => onViewChange('week')}
                size={isMobile ? "small" : "medium"}
              >
                {isMobile ? "S" : "Semana"}
              </Button>
              <Button
                variant={currentView === 'day' ? 'contained' : 'outlined'}
                color="primary"
                startIcon={!isMobile && <CalendarViewDayIcon />}
                onClick={() => onViewChange('day')}
                size={isMobile ? "small" : "medium"}
              >
                {isMobile ? "D" : "Día"}
              </Button>
            </Stack>
          </Grid>

          {/* Filtro por especialidad */}
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size={isMobile ? "small" : "medium"}>
              <InputLabel sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 0.5,
                color: filtersLocked ? 'text.disabled' : 'text.primary'
              }}>
                {filtersLocked && <LockIcon fontSize="small" />}
                Especialidad
              </InputLabel>
              <Select
                value={selectedEspecialidad}
                label={filtersLocked ? '🔒 Especialidad' : 'Especialidad'}
                onChange={onEspecialidadChange}
                disabled={filtersLocked}  // ✅ AGREGAR ESTA LÍNEA
                sx={{
                  '& .MuiSelect-select': {
                    color: filtersLocked ? 'text.disabled' : 'text.primary'
                  }
                }}
              >
                <MenuItem value="">Todas</MenuItem>
                {especialidades.map((esp) => (
                  <MenuItem key={esp.id} value={esp.id}>
                    {esp.nombre}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Filtro por especialista */}
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size={isMobile ? "small" : "medium"}>
            <InputLabel sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 0.5,
              color: filtersLocked ? 'text.disabled' : 'text.primary'
            }}>
              {filtersLocked && <LockIcon fontSize="small" />}
              Especialista
            </InputLabel>
            <Select
              value={selectedEspecialista}
              label={filtersLocked ? '🔒 Especialista' : 'Especialista'}
              onChange={onEspecialistaChange}
              disabled={filtersLocked || loadingHorarios}  // ✅ MODIFICAR ESTA LÍNEA
              sx={{
                '& .MuiSelect-select': {
                  color: filtersLocked ? 'text.disabled' : 'text.primary'
                }
              }}
            >
              <MenuItem value="">Todos</MenuItem>
              {especialistas.map((especialista) => (
                <MenuItem key={especialista.id} value={especialista.id}>
                  {especialista.usuario?.nombre} {especialista.usuario?.apellidos}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          </Grid>
        </Grid>
        
        {/* Título del período actual */}
        <Box mt={2} textAlign="center">
          <Typography variant="h6">
            {getPeriodTitle()}
          </Typography>
        </Box>
      
      {filtersLocked && (
  <Box 
    mt={2}
    sx={{ 
      background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.1) 0%, rgba(33, 150, 243, 0.05) 100%)',
      borderRadius: '12px',
      padding: '12px 16px',
      border: '1px solid rgba(33, 150, 243, 0.2)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 1
    }}
  >
    <LockIcon fontSize="small" color="primary" />
    <Typography variant="body2" color="primary" sx={{ fontWeight: '600' }}>
      Filtros aplicados automáticamente según su rol
    </Typography>
    <InfoIcon fontSize="small" color="action" />
  </Box>
)}

        {/* Indicador de carga de horarios */}
        {loadingHorarios && (
          <Box mt={1} textAlign="center">
            <Chip 
              label="Cargando horarios..." 
              size="small" 
              variant="outlined"
              color="info"
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default CalendarControls;